import { JobLogModel } from '@/types';
import { timeConverter } from '@/utils/helpers';
import { Descriptions, Empty, Tooltip, Typography } from 'antd';
import React, { FC, useMemo } from 'react';

interface LogsTimelineProps {
  logs: JobLogModel[];
}

const LogHoverContent: FC<{ log: JobLogModel }> = ({ log }) => (
  <Descriptions
    bordered
    size="small"
    column={1}
    className="logs-timeline__tooltip-desc mb-0"
    styles={{ label: { width: 120 } }}
    items={[
      { key: 'latitude', label: 'Latitude', children: log.latititude },
      { key: 'longitude', label: 'Longitude', children: log.longitude },
      {
        key: 'accuracy',
        label: 'Accuracy',
        children: log.accuracy ?? '—',
      },
      {
        key: 'elevation',
        label: 'Elevation',
        children: log.elevation ?? '—',
      },
      {
        key: 'verifiedBy',
        label: 'Verified by',
        children: log.verifiedBy || '—',
      },
      {
        key: 'verifiedAt',
        label: 'Verified at',
        children: log.verifiedAt ? timeConverter(log.verifiedAt) : '—',
      },
      {
        key: 'note',
        label: 'Verified at',
        children: log.note || '—',
      },
    ]}
  />
);

export const LogsTimeline: FC<LogsTimelineProps> = ({ logs }) => {
  const sortedLogs = useMemo(() => {
    const list = [...(logs || [])];
    list.sort(
      (a, b) =>
        new Date(a.performedAt).getTime() - new Date(b.performedAt).getTime(),
    );
    return list;
  }, [logs]);

  if (!sortedLogs.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No logs" />;
  }

  return (
    <div
      className="logs-timeline d-flex flex-wrap align-items-stretch gap-2"
      role="list"
    >
      {sortedLogs.map((log, index) => (
        <React.Fragment key={log.id}>
          {index > 0 ? (
            <div
              className="logs-timeline__connector align-self-center"
              aria-hidden
            />
          ) : null}
          <Tooltip
            title={<LogHoverContent log={log} />}
            placement="topLeft"
            mouseEnterDelay={0.15}
            rootClassName="logs-timeline__tooltip-overlay"
          >
            <div
              className="logs-timeline__item flex-shrink-0"
              role="listitem"
              tabIndex={0}
            >
              <span className="logs-timeline__dot" aria-hidden />
              <div className="logs-timeline__body">
                <Typography.Text strong className="logs-timeline__status">
                  {log.status}
                </Typography.Text>
                <Typography.Text
                  type="secondary"
                  className="logs-timeline__by d-block"
                >
                  {log.performedBy}
                </Typography.Text>
                <Typography.Text type="secondary" className="logs-timeline__at">
                  {timeConverter(log.performedAt)}
                </Typography.Text>
              </div>
            </div>
          </Tooltip>
        </React.Fragment>
      ))}
    </div>
  );
};
